// ABOUTME: Built-in date-bucket granularities for ShDataGrid3 row grouping — 15m / 30m / 1h / 4h / day / week / month.
// ABOUTME: Frontend-only. A column opts in via `grouping: { type: "date", granularities: [...] }`; omit `granularities` to get all.

/**
 * Each entry describes one way to bucket a date/datetime value for grouping.
 *
 * Shape:
 *   id            — stable key persisted in userLayoutSlice (`groupBy.granularity`)
 *   label         — i18n key; consumers render via t(label) in the "Group by" context menu
 *   bucket(value) — takes the raw row value (string or Date) and returns a stable
 *                   bucket key (epoch-ms number of the bucket start). Rows that
 *                   resolve to the same bucket key land in the same group.
 *   formatKey(key, locale)
 *                 — default header-label formatter, takes the bucket key and a
 *                   locale string and returns a human-readable string. Call
 *                   sites can override via column.grouping.renderLabel.
 */

const toDate = (value) => {
    if (value == null) return null;
    const d = value instanceof Date ? value : new Date(value);
    return Number.isFinite(d.getTime()) ? d : null;
};

// Round a timestamp down to the nearest `sizeMs` multiple. Used for sub-hour buckets.
const floorTo = (ts, sizeMs) => Math.floor(ts / sizeMs) * sizeMs;

const MS_MINUTE = 60 * 1000;
const MS_HOUR = 60 * MS_MINUTE;
const MS_DAY = 24 * MS_HOUR;

const formatDateTime = (key, locale) => {
    const d = new Date(key);
    return `${d.toLocaleDateString(locale, { dateStyle: "short" })} ${d.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" })}`;
};

const formatDate = (key, locale) => {
    const d = new Date(key);
    return d.toLocaleDateString(locale, { dateStyle: "medium" });
};

const formatMonth = (key, locale) => {
    const d = new Date(key);
    return d.toLocaleDateString(locale, { year: "numeric", month: "long" });
};

const startOfWeek = (d) => {
    // ISO week: Monday as day 1. JS getDay(): Sun=0..Sat=6.
    const copy = new Date(d);
    const jsDay = copy.getDay();
    const isoDay = jsDay === 0 ? 7 : jsDay;
    copy.setHours(0, 0, 0, 0);
    copy.setDate(copy.getDate() - (isoDay - 1));
    return copy.getTime();
};

const startOfDay = (d) => {
    const copy = new Date(d);
    copy.setHours(0, 0, 0, 0);
    return copy.getTime();
};

const startOfMonth = (d) => {
    const copy = new Date(d);
    copy.setDate(1);
    copy.setHours(0, 0, 0, 0);
    return copy.getTime();
};

const GRANULARITIES = {
    "15m": {
        id: "15m",
        label: "15_minutes",
        bucket: (value) => {
            const d = toDate(value);
            return d ? floorTo(d.getTime(), 15 * MS_MINUTE) : null;
        },
        formatKey: formatDateTime,
    },
    "30m": {
        id: "30m",
        label: "30_minutes",
        bucket: (value) => {
            const d = toDate(value);
            return d ? floorTo(d.getTime(), 30 * MS_MINUTE) : null;
        },
        formatKey: formatDateTime,
    },
    "1h": {
        id: "1h",
        label: "hour",
        bucket: (value) => {
            const d = toDate(value);
            return d ? floorTo(d.getTime(), MS_HOUR) : null;
        },
        formatKey: formatDateTime,
    },
    "4h": {
        id: "4h",
        label: "4_hours",
        bucket: (value) => {
            const d = toDate(value);
            return d ? floorTo(d.getTime(), 4 * MS_HOUR) : null;
        },
        formatKey: formatDateTime,
    },
    day: {
        id: "day",
        label: "day",
        bucket: (value) => {
            const d = toDate(value);
            return d ? startOfDay(d) : null;
        },
        formatKey: formatDate,
    },
    week: {
        id: "week",
        label: "week",
        bucket: (value) => {
            const d = toDate(value);
            return d ? startOfWeek(d) : null;
        },
        formatKey: formatDate,
    },
    month: {
        id: "month",
        label: "month",
        bucket: (value) => {
            const d = toDate(value);
            return d ? startOfMonth(d) : null;
        },
        formatKey: formatMonth,
    },
};

/**
 * Resolve the granularity objects the call site wants for a given column.
 * If `whitelist` is an array, return those (in order). Otherwise return all.
 */
const resolveDateGranularities = (whitelist) => {
    if (Array.isArray(whitelist) && whitelist.length > 0) {
        return whitelist
            .map((id) => GRANULARITIES[id])
            .filter(Boolean);
    }
    return Object.values(GRANULARITIES);
};

const GRANULARITY_KEYS = Object.freeze({
    MIN_15: "15m",
    MIN_30: "30m",
    HOUR: "1h",
    HOUR_4: "4h",
    DAY: "day",
    // WEEK: "week",
    // MONTH: "month",
});

export { GRANULARITIES, GRANULARITY_KEYS, resolveDateGranularities };
